/**
 * Camelot Wheel Utilities
 *
 * The Camelot Wheel is a tool used by DJs for harmonic mixing.
 * Keys are labeled 1A-12A (minor) and 1B-12B (major).
 */

/** Spotify key (0-11) to pitch class name */
const PITCH_CLASSES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;

/** Spotify key + mode to Camelot notation */
const CAMELOT_MAP: Record<string, string> = {
  // Minor keys (mode = 0)
  '0_0': '5A',  // C minor
  '1_0': '12A', // C# minor
  '2_0': '7A',  // D minor
  '3_0': '2A',  // D# minor
  '4_0': '9A',  // E minor
  '5_0': '4A',  // F minor
  '6_0': '11A', // F# minor
  '7_0': '6A',  // G minor
  '8_0': '1A',  // G# minor
  '9_0': '8A',  // A minor
  '10_0': '3A', // A# minor
  '11_0': '10A', // B minor

  // Major keys (mode = 1)
  '0_1': '8B',  // C major
  '1_1': '3B',  // C# major
  '2_1': '10B', // D major
  '3_1': '5B',  // D# major
  '4_1': '12B', // E major
  '5_1': '7B',  // F major
  '6_1': '2B',  // F# major
  '7_1': '9B',  // G major
  '8_1': '4B',  // G# major
  '9_1': '11B', // A major
  '10_1': '6B', // A# major
  '11_1': '1B', // B major
};

/** Reverse map: Camelot to Spotify key + mode */
const REVERSE_CAMELOT_MAP: Record<string, { key: number; mode: 0 | 1 }> = Object.fromEntries(
  Object.entries(CAMELOT_MAP).map(([keyMode, camelot]) => {
    const parts = keyMode.split('_').map(Number);
    const key = parts[0] ?? 0;
    const mode = (parts[1] ?? 0) as 0 | 1;
    return [camelot, { key, mode }];
  })
);

/**
 * Convert Spotify key (0-11) and mode (0=minor, 1=major) to Camelot notation
 */
export function toCamelot(key: number, mode: 0 | 1): string {
  return CAMELOT_MAP[`${key}_${mode}`] || 'Unknown';
}

/**
 * Convert Camelot notation to Spotify key and mode
 */
export function fromCamelot(camelot: string): { key: number; mode: 0 | 1 } | null {
  return REVERSE_CAMELOT_MAP[camelot.toUpperCase()] || null;
}

/**
 * Get the pitch class name (C, C#, D, etc.)
 */
export function getPitchClass(key: number): string {
  return PITCH_CLASSES[key] || 'Unknown';
}

/**
 * Get the full key name (e.g., "A minor", "C major")
 */
export function getKeyName(key: number, mode: 0 | 1): string {
  const pitch = getPitchClass(key);
  const modeName = mode === 1 ? 'major' : 'minor';
  return `${pitch} ${modeName}`;
}

/**
 * Parse Camelot key string to number and letter
 */
export function parseCamelot(camelot: string): { number: number; letter: 'A' | 'B' } | null {
  const match = camelot.toUpperCase().match(/^(\d{1,2})([AB])$/);
  if (!match) return null;
  const number = parseInt(match[1]!, 10);
  if (number < 1 || number > 12) return null;
  return { number, letter: match[2]! as 'A' | 'B' };
}

/**
 * Get compatible Camelot keys for harmonic mixing
 * Returns keys that mix well: same key, +1, -1, and relative major/minor
 */
export function getCompatibleKeys(camelot: string): string[] {
  const parsed = parseCamelot(camelot);
  if (!parsed) return [];

  const { number, letter } = parsed;
  const compatible: string[] = [];

  // Same key
  compatible.push(`${number}${letter}`);

  // +1 (up the wheel)
  const up = number === 12 ? 1 : number + 1;
  compatible.push(`${up}${letter}`);

  // -1 (down the wheel)
  const down = number === 1 ? 12 : number - 1;
  compatible.push(`${down}${letter}`);

  // Relative major/minor (same number, opposite letter)
  const opposite = letter === 'A' ? 'B' : 'A';
  compatible.push(`${number}${opposite}`);

  return compatible;
}

/**
 * Get all Camelot keys within a certain distance
 * Useful for SmartSlot filtering with adjustable strictness
 */
export function getAllKeysWithinDistance(camelot: string, maxDistance: number): string[] {
  const parsed = parseCamelot(camelot);
  if (!parsed) return [];

  const allKeys: string[] = [];

  // Generate all possible Camelot keys
  for (let num = 1; num <= 12; num++) {
    for (const letter of ['A', 'B'] as const) {
      const key = `${num}${letter}`;
      const distance = getKeyDistance(camelot, key);
      if (distance <= maxDistance) {
        allKeys.push(key);
      }
    }
  }

  return allKeys;
}

/**
 * Check if two Camelot keys are harmonically compatible
 */
export function areKeysCompatible(key1: string, key2: string): boolean {
  const compatible = getCompatibleKeys(key1);
  return compatible.includes(key2.toUpperCase());
}

/**
 * Calculate harmonic "distance" between two Camelot keys
 * Lower is better: 0 = same key, 1 = adjacent, 2 = relative, higher = less compatible
 */
export function getKeyDistance(key1: string, key2: string): number {
  const p1 = parseCamelot(key1);
  const p2 = parseCamelot(key2);
  if (!p1 || !p2) return Infinity;

  // Same key
  if (p1.number === p2.number && p1.letter === p2.letter) return 0;

  // Adjacent on wheel (same letter)
  if (p1.letter === p2.letter) {
    const diff = Math.abs(p1.number - p2.number);
    if (diff === 1 || diff === 11) return 1;
  }

  // Relative major/minor (same number, different letter)
  if (p1.number === p2.number && p1.letter !== p2.letter) return 1;

  // Two steps on wheel
  if (p1.letter === p2.letter) {
    const diff = Math.abs(p1.number - p2.number);
    if (diff === 2 || diff === 10) return 2;
  }

  // Further steps
  const circularDiff = Math.min(
    Math.abs(p1.number - p2.number),
    12 - Math.abs(p1.number - p2.number)
  );

  // Same letter = use circular diff
  if (p1.letter === p2.letter) return circularDiff;

  // Different letter = add penalty
  return circularDiff + 1;
}

/**
 * Get CSS color for a Camelot key (for UI badges)
 */
export function getCamelotColor(camelot: string): string {
  const parsed = parseCamelot(camelot);
  if (!parsed) return 'bg-zinc-700 text-zinc-300';

  const colors: Record<number, string> = {
    1: 'bg-red-500/20 text-red-300 border-red-500/30',
    2: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    3: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    4: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    5: 'bg-lime-500/20 text-lime-300 border-lime-500/30',
    6: 'bg-green-500/20 text-green-300 border-green-500/30',
    7: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    8: 'bg-teal-500/20 text-teal-300 border-teal-500/30',
    9: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
    10: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
    11: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    12: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
  };

  return colors[parsed.number] || 'bg-zinc-700 text-zinc-300';
}
